import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import { inventoryManagementService } from "../../../services/inventoryManagementService";

function resolveImageSrc(item) {
  if (!item) return null;
  if (item.url) return item.url;
  const fileByte =
    item.fileByte ||
    item.base64 ||
    (typeof item.value === "string" ? item.value : item.value?.fileByte);
  if (!fileByte) return null;
  const text = String(fileByte);
  if (
    text.startsWith("data:") ||
    text.startsWith("blob:") ||
    text.startsWith("http")
  ) {
    return text;
  }
  const contentType =
    item.contentType || item.value?.contentType || "image/jpeg";
  return `data:${contentType};base64,${text}`;
}

function getApiErrorMessage(err, fallback) {
  if (!err) return fallback;
  const candidates = [
    err.message,
    typeof err.data === "string" ? err.data : null,
    err.data?.message,
  ];
  for (const candidate of candidates) {
    const text = candidate == null ? "" : String(candidate).trim();
    if (text && text !== "[object Object]") return text;
  }
  return fallback;
}

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   inventoryInId?: string | number | null,
 *   mode?: "defective" | "inventory",
 * }} props
 */
export function StockJournalImagesModal({
  open,
  onOpenChange,
  inventoryInId = null,
  mode = "inventory",
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [images, setImages] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [preview, setPreview] = useState(null);

  const isDefective = mode === "defective";
  const title = isDefective
    ? t("inventory.defectiveImagesAndRemarks")
    : t("inventory.inventoryImages");

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setError("");
      setImages([]);
      setRemarks("");
      setPreview(null);
      return;
    }

    const id = inventoryInId != null ? String(inventoryInId).trim() : "";
    if (!id) {
      setError(t("inventory.inventoryInIdMissingOnJournal"));
      setImages([]);
      setRemarks("");
      return;
    }

    let active = true;
    setLoading(true);
    setError("");
    setImages([]);
    setRemarks("");
    setPreview(null);

    const imagesPromise = isDefective
      ? inventoryManagementService.getInventoryRecordDefectiveImages(id)
      : inventoryManagementService.getInventoryRecordImages(id);

    const remarksPromise = isDefective
      ? inventoryManagementService
          .getInventoryRecordById(id)
          .then((record) => record?.remarks || "")
          .catch(() => "")
      : Promise.resolve("");

    Promise.all([imagesPromise, remarksPromise])
      .then(([imageList, recordRemarks]) => {
        if (!active) return;
        setImages(Array.isArray(imageList) ? imageList : []);
        setRemarks(String(recordRemarks || "").trim());
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load journal images:", err);
        setError(
          getApiErrorMessage(err, t("inventory.failedToLoadJournalImages"))
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, inventoryInId, isDefective, t]);

  const closePreview = useCallback(() => setPreview(null), []);

  const openPreview = useCallback(
    (item) => {
      const src = resolveImageSrc(item);
      if (!src) return;
      setPreview({
        name: item.name || t("inventory.imagePreviewTitle"),
        src,
      });
    },
    [t]
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-[min(94vw,640px)] max-h-[85vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border bg-white p-5 shadow-xl focus:outline-hidden">
            <div className="mb-4 flex items-center justify-between gap-3">
              <DialogTitle className="text-base font-semibold text-foreground">
                {title}
              </DialogTitle>
              <DialogPrimitive.Close
                className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition cursor-pointer"
                aria-label={t("formActions.close")}
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            {loading ? (
              <div className="py-10 text-center text-sm per-file-hint">
                {t("common.loading")}
              </div>
            ) : error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">
                {error}
              </div>
            ) : (
              <div className="space-y-4">
                {isDefective ? (
                  <div className="rounded-xl border border-(--color-text-border)/50 bg-workspace/30 px-3.5 py-3">
                    <p className="text-xs font-semibold per-file-hint mb-1.5">
                      {t("common.remarks")}
                    </p>
                    <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                      {remarks || "—"}
                    </p>
                  </div>
                ) : null}

                {images.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-8 text-sm per-file-hint">
                    <ImageIcon className="h-8 w-8 opacity-40" />
                    <span>{t("inventory.noImagesAvailable")}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {images.map((item) => {
                      const src = resolveImageSrc(item);
                      return (
                        <button
                          key={item.key || item.name}
                          type="button"
                          onClick={() => openPreview(item)}
                          title={t("inventory.viewImage")}
                          className="group relative aspect-square overflow-hidden rounded-xl border border-(--color-text-border)/50 bg-workspace/40 cursor-pointer hover:border-primary/40 transition"
                        >
                          {src ? (
                            <img
                              src={src}
                              alt={item.name || t("inventory.imagePreviewTitle")}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="grid h-full place-items-center text-xs per-file-hint px-2 text-center">
                              {item.name || t("inventory.viewImage")}
                            </span>
                          )}
                          <span className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-2 py-1 text-[10px] font-medium text-white opacity-0 group-hover:opacity-100 transition">
                            {item.name || t("inventory.viewImage")}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      <Dialog
        open={Boolean(preview)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closePreview();
        }}
      >
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-[60] w-[min(92vw,720px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border bg-white p-4 shadow-xl focus:outline-hidden">
            <div className="mb-3 flex items-center justify-between gap-3">
              <DialogTitle className="truncate text-sm font-semibold text-foreground">
                {preview?.name || t("inventory.imagePreviewTitle")}
              </DialogTitle>
              <DialogPrimitive.Close
                onClick={closePreview}
                className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition cursor-pointer"
                aria-label={t("formActions.close")}
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>
            {preview?.src ? (
              <div className="max-h-[70vh] overflow-auto rounded-xl bg-workspace/40 p-2">
                <img
                  src={preview.src}
                  alt={preview.name || t("inventory.imagePreviewTitle")}
                  className="mx-auto max-h-[65vh] w-auto max-w-full object-contain"
                />
              </div>
            ) : null}
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </>
  );
}
